import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GavelIcon from "@mui/icons-material/Gavel";
import ArticleIcon from "@mui/icons-material/Article";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import AppDialog from "../General/Dialog/AppDialog";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";

import {
  getTypeSources,
  createTypeSource,
  updateTypeSource,
  deleteTypeSource,
} from "../../services/AttachmentTypeSourceConfigService";
import { getAllSourceTypeDefinitions } from "../../services/AttachmentSourceTypeService";
import { useToast } from "../../context/ToastContext";

const ICON_MAP = {
  gavel:         <GavelIcon fontSize="small" />,
  upload_file:   <UploadFileIcon fontSize="small" />,
  article:       <ArticleIcon fontSize="small" />,
  auto_fix_high: <AutoFixHighIcon fontSize="small" />,
  attach_file:   <AttachFileIcon fontSize="small" />,
};

const EMPTY_FORM = {
  source_type: "",
  is_required: true,
  is_user_selectable: false,
  upload_order: 0,
  description: "",
};

function SourceForm({ initial, onSave, onCancel, saving, sourceTypeDefs = [] }) {
  const [form, setForm] = useState(initial ?? EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.source_type) e.source_type = "Seleccioná un tipo.";
    return e;
  };

  const handleSave = () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    const def = sourceTypeDefs.find((d) => d.source_type === form.source_type);
    onSave({ ...form, label: def?.label ?? form.source_type });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.5 }}>
        {initial ? "Editar fuente" : "Nueva fuente"}
      </Typography>

      <FormControl size="small" fullWidth error={!!errors.source_type}>
        <InputLabel>Tipo de fuente *</InputLabel>
        <Select
          value={form.source_type}
          onChange={(e) => set("source_type", e.target.value)}
          label="Tipo de fuente *"
        >
          {sourceTypeDefs.map((def) => (
            <MenuItem key={def.source_type} value={def.source_type}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {ICON_MAP[def.icon_key] ?? <AttachFileIcon fontSize="small" />}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {def.label}
                  </Typography>
                  {def.description && (
                    <Typography variant="caption" sx={{ opacity: 0.55 }}>
                      {def.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Select>
        {errors.source_type && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
            {errors.source_type}
          </Typography>
        )}
      </FormControl>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          label="Orden de carga"
          size="small"
          type="number"
          value={form.upload_order}
          onChange={(e) => set("upload_order", Number(e.target.value))}
          sx={{ width: 130 }}
          inputProps={{ min: 0 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={form.is_required}
              onChange={(e) => set("is_required", e.target.checked)}
              size="small"
            />
          }
          label={<Typography variant="body2">Requerido</Typography>}
        />
        <FormControlLabel
          control={
            <Switch
              checked={form.is_user_selectable}
              onChange={(e) => set("is_user_selectable", e.target.checked)}
              size="small"
            />
          }
          label={<Typography variant="body2">Seleccionable por usuario</Typography>}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <Button
          size="small"
          onClick={onCancel}
          disabled={saving}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
        >
          Cancelar
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveOutlinedIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
        >
          Guardar
        </Button>
      </Box>
    </Box>
  );
}

export default function AttachmentTypeSourceManager({ open, onClose, attachmentType }) {
  const { showToast } = useToast();
  const [sources, setSources] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const [sourceTypeDefs, setSourceTypeDefs] = useState([]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deletingSource, setDeletingSource] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    if (!attachmentType) return;
    setLoadingList(true);
    try {
      const [sourcesRes, defsRes] = await Promise.all([
        getTypeSources(attachmentType.id),
        getAllSourceTypeDefinitions(),
      ]);
      setSources(sourcesRes?.data?.data ?? []);
      setSourceTypeDefs(defsRes?.data?.data ?? []);
    } catch {
      showToast("Error al cargar los datos.", "error");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (open && attachmentType) {
      load();
      setShowAddForm(false);
      setEditingId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, attachmentType]);

  const handleCreate = async (form) => {
    setSaving(true);
    try {
      await createTypeSource(attachmentType.id, form);
      setShowAddForm(false);
      await load();
    } catch (e) {
      showToast(e?.response?.data?.message ?? "Error al crear la fuente.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (form) => {
    setSaving(true);
    try {
      await updateTypeSource(attachmentType.id, editingId, form);
      setEditingId(null);
      await load();
    } catch (e) {
      showToast(e?.response?.data?.message ?? "Error al actualizar la fuente.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await deleteTypeSource(attachmentType.id, deletingSource.id);
      setConfirmDeleteOpen(false);
      setDeletingSource(null);
      await load();
    } catch (e) {
      showToast(e?.response?.data?.message ?? "Error al eliminar la fuente.", "error");
    } finally {
      setDeleting(false);
    }
  };

  const getSourceMeta = (sourceType) => {
    const def = sourceTypeDefs.find((d) => d.source_type === sourceType);
    return def
      ? { icon: ICON_MAP[def.icon_key] ?? <InfoOutlinedIcon fontSize="small" />, label: def.label }
      : { icon: <InfoOutlinedIcon fontSize="small" />, label: sourceType };
  };

  return (
    <>
      <AppDialog
        open={open}
        onClose={onClose}
        title={`Fuentes de documentos`}
        subtitle={attachmentType?.display_name || attachmentType?.name}
        maxWidth="sm"
        fullWidth
        fullScreenMobile
        fullScreenBreakpoint="sm"
      >

        {loadingList ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {sources.length === 0 && !showAddForm && (
              <Typography
                variant="body2"
                sx={{ opacity: 0.6, textAlign: "center", py: 2 }}
              >
                No hay fuentes configuradas. Agregá la primera.
              </Typography>
            )}

            {sources.map((source) =>
              editingId === source.id ? (
                <SourceForm
                  key={source.id}
                  initial={source}
                  onSave={handleUpdate}
                  onCancel={() => setEditingId(null)}
                  saving={saving}
                  sourceTypeDefs={sourceTypeDefs}
                />
              ) : (
                <Box
                  key={source.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1.2,
                    borderRadius: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box sx={{ color: "primary.main", display: "flex", alignItems: "center" }}>
                    {getSourceMeta(source.source_type).icon}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                      {getSourceMeta(source.source_type).label}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                      Orden: {source.upload_order}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexShrink: 0 }}>
                    <Chip
                      label={source.is_required ? "Requerido" : "Opcional"}
                      size="small"
                      color={source.is_required ? "error" : "default"}
                      variant="outlined"
                      sx={{ fontSize: 11, height: 20, fontWeight: 700 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => { setShowAddForm(false); setEditingId(source.id); }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => { setDeletingSource(source); setConfirmDeleteOpen(true); }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )
            )}

            {showAddForm ? (
              <SourceForm
                onSave={handleCreate}
                onCancel={() => setShowAddForm(false)}
                saving={saving}
                sourceTypeDefs={sourceTypeDefs}
              />
            ) : (
              <Button
                startIcon={<AddIcon />}
                onClick={() => { setEditingId(null); setShowAddForm(true); }}
                sx={{
                  mt: 0.5,
                  textTransform: "none",
                  fontWeight: 800,
                  borderRadius: 2.5,
                  alignSelf: "flex-start",
                }}
              >
                Agregar fuente
              </Button>
            )}
          </Box>
        )}
      </AppDialog>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onClose={() => { setConfirmDeleteOpen(false); setDeletingSource(null); }}
        onConfirm={handleDeleteConfirm}
        title="Eliminar fuente"
        description={`¿Eliminar la fuente "${deletingSource?.label}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        confirmColor="error"
        loading={deleting}
      />
    </>
  );
}
